/**
 * AnalysisPage — Dedicated page for video analysis (ingest).
 *
 * Shows all video assets in the selected workspace with their analysis
 * status, progress bars, and actions. This is the step between Upload
 * and Create AI Draft.
 *
 * Design mirrors the Collections/Clips page: full-width table layout,
 * stats bar, column headers, and responsive grid.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { isVideoAsset } from '../utils/mediaType'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Cpu, CheckCircle, RotateCcw, Loader2, ArrowRight, Film,
  ChevronDown, Layers, Play, Hourglass, Sparkles, Trash2,
  ArrowRightLeft, X, LayoutList,
} from 'lucide-react'
import ViewToggle from '../components/ViewToggle'
import toast from 'react-hot-toast'
import apiClient from '../services/api'
import GlobalSearch from '../components/GlobalSearch'
import MediaPreviewModal from '../components/MediaPreviewModal'
import { useMediaPreview } from '../hooks/useMediaPreview'
import { assetApi } from '../services/assetFolderApi'
import type { Asset } from '../types/fileManager'
import { apiErrorDetail } from '../utils/dataValidator'

interface Project {
  id: string
  name: string
}

interface IngestStatus {
  status: string
  step: string
  progress: number
  moments_count: number
  error?: string
}

interface MomentItem {
  id: number
  asset_id: number | null
  scene_index: number
  start_time: number | null
  end_time: number | null
  title: string | null
  visual_description: string | null
  audio_description: string | null
  thumbnail_url: string | null
  tags: string[] | null
  notes: string | null
  created_time: string | null
  asset_filename: string | null
  asset_file_type: string | null
  asset_duration: number | null
}

interface ClusterMomentBrief {
  id: number
  scene_index: number
  title: string | null
  thumbnail_url: string | null
  start_time: number | null
  end_time: number | null
  asset_id: number | null
  asset_filename: string | null
}

interface ClusterItem {
  id: number
  project_id: string
  name: string
  description: string | null
  moments_count: number
  moments: ClusterMomentBrief[]
  created_time: string | null
}

interface ScoredMoment {
  id: number
  asset_id: number | null
  scene_index: number
  start_time: number | null
  end_time: number | null
  title: string | null
  visual_description: string | null
  audio_description: string | null
  thumbnail_url: string | null
  tags: string[] | null
  created_time: string | null
  asset_filename: string | null
  asset_file_type: string | null
  asset_duration: number | null
  event_score: number | null
  score_breakdown: { audio: number; motion: number; speaker: number; total: number } | null
  discarded: boolean
}

const STEP_LABELS: Record<string, string> = {
  queued: 'Queued',
  downloading: 'Downloading video',
  analyzing: 'Reading video info',
  detecting_shots: 'Detecting shots',
  extracting_signals: 'Extracting signals',
  transcribing: 'Transcribing speech',
  extracting_thumbnails: 'Extracting thumbnails',
  complete: 'Complete',
  failed: 'Failed',
}

function formatDuration(secs: number | null | undefined): string {
  if (secs == null) return '--'
  const m = Math.floor(secs / 60)
  const s = Math.round(secs % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

function formatDurationHMS(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '00:00:00'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

function formatTotalDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)} sec`
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`
  const h = Math.floor(seconds / 3600)
  const m = Math.round((seconds % 3600) / 60)
  return m > 0 ? `${h} hr ${m} min` : `${h} hr`
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '--'
  const d = new Date(dateStr)
  return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`
}

export default function AnalysisPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const prefillProjectId = searchParams.get('project_id') || ''

  const [projects, setProjects] = useState<Project[]>([])
  const [projectsLoading, setProjectsLoading] = useState(true)
  const [selectedProjectId, setSelectedProjectId] = useState(prefillProjectId)
  const [showProjectPicker, setShowProjectPicker] = useState(false)
  const { preview, closePreview, openFromMoment, openFromAsset } = useMediaPreview()

  const [assets, setAssets] = useState<Asset[]>([])
  const [assetsLoading, setAssetsLoading] = useState(false)

  // Per-asset ingest status (from polling)
  const [statuses, setStatuses] = useState<Record<number, IngestStatus>>({})

  // View mode
  const [viewMode, setViewMode] = useState<'all-clips' | 'by-video' | 'themes' | 'discarded'>('by-video')

  // All clips view
  const [layoutMode, setLayoutMode] = useState<'list' | 'grid'>('list')
  const [allMoments, setAllMoments] = useState<MomentItem[]>([])
  const [allMomentsTotal, setAllMomentsTotal] = useState(0)
  const [allMomentsLoading, setAllMomentsLoading] = useState(false)

  // Expandable clips per asset (by-video mode)
  const [expandedAssetId, setExpandedAssetId] = useState<number | null>(null)
  const [assetMoments, setAssetMoments] = useState<Record<number, MomentItem[]>>({})
  const [momentsLoading, setMomentsLoading] = useState(false)

  // Themes view (auto-generated clusters)
  const [themes, setThemes] = useState<ClusterItem[]>([])
  const [themesLoading, setThemesLoading] = useState(false)
  const [themesGenerating, setThemesGenerating] = useState(false)
  const [expandedThemeId, setExpandedThemeId] = useState<number | null>(null)

  // Discarded view (low-scoring clips)
  const [scoredMoments, setScoredMoments] = useState<ScoredMoment[]>([])
  const [evaluating, setEvaluating] = useState(false)

  // Relocate modals
  const [relocateVideoAssetId, setRelocateVideoAssetId] = useState<number | null>(null)
  const [relocateClipMomentId, setRelocateClipMomentId] = useState<number | null>(null)
  const [relocating, setRelocating] = useState(false)

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Derived: only video assets (MIME type with extension fallback)
  const videoAssets = useMemo(
    () => assets.filter(isVideoAsset),
    [assets],
  )

  // Merge polling status with asset.status for display
  const getEffectiveStatus = useCallback((asset: Asset): string => {
    return statuses[asset.id]?.status || asset.status || 'uploaded'
  }, [statuses])

  // Counts
  const readyCount = videoAssets.filter(a => getEffectiveStatus(a) === 'ready').length
  const ingestingCount = videoAssets.filter(a => getEffectiveStatus(a) === 'ingesting').length
  const unanalyzedAssets = videoAssets.filter(a => {
    const s = getEffectiveStatus(a)
    return s === 'uploaded' || s === 'failed'
  })

  // All-clips stats
  const distinctVideoCount = useMemo(
    () => new Set(allMoments.map(m => m.asset_id).filter(Boolean)).size,
    [allMoments],
  )
  const totalClipDuration = useMemo(
    () => allMoments.reduce((sum, m) => {
      if (m.start_time !== null && m.end_time !== null) return sum + (m.end_time - m.start_time)
      return sum
    }, 0),
    [allMoments],
  )

  // Selected project name for the picker button
  const selectedProjectName = useMemo(
    () => projects.find(p => p.id === selectedProjectId)?.name,
    [projects, selectedProjectId],
  )

  // ── Fetch projects ──
  useEffect(() => {
    const load = async () => {
      try {
        setProjectsLoading(true)
        const res = await apiClient.get('/projects/')
        setProjects((res.data.projects || []).map((p: { id: number; name: string }) => ({ id: p.id, name: p.name })))
      } catch {
        toast.error('Failed to load workspaces')
      } finally {
        setProjectsLoading(false)
      }
    }
    load()
  }, [])

  // ── Fetch assets when project changes ──
  useEffect(() => {
    if (!selectedProjectId) {
      setAssets([])
      setStatuses({})
      setExpandedAssetId(null)
      setAssetMoments({})
      setAllMoments([])
      setAllMomentsTotal(0)
      setThemes([])
      setExpandedThemeId(null)
      setScoredMoments([])
      return
    }
    let cancelled = false
    const load = async () => {
      setAssetsLoading(true)
      try {
        const data = await assetApi.list(selectedProjectId, { limit: 200 })
        if (!cancelled) setAssets(data.assets)
      } catch {
        if (!cancelled) toast.error('Failed to load assets')
      } finally {
        if (!cancelled) setAssetsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [selectedProjectId])

  // ── Fetch initial ingest status for all video assets ──
  useEffect(() => {
    if (videoAssets.length === 0) return
    let cancelled = false
    const load = async () => {
      const results = await Promise.allSettled(
        videoAssets.map(a => apiClient.get(`/ingest/${a.id}/status`)),
      )
      if (cancelled) return
      const next: Record<number, IngestStatus> = {}
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          next[videoAssets[i].id] = r.value.data
        }
      })
      setStatuses(next)
    }
    load()
    return () => { cancelled = true }
  }, [videoAssets, selectedProjectId])

  // ── Fetch all moments for "All Clips" view ──
  useEffect(() => {
    if (viewMode !== 'all-clips' || !selectedProjectId || readyCount === 0) return
    let cancelled = false
    const load = async () => {
      setAllMomentsLoading(true)
      try {
        const res = await apiClient.get('/moments/', {
          params: { project_ids: selectedProjectId, limit: 200 },
        })
        if (!cancelled) {
          setAllMoments(res.data.moments)
          setAllMomentsTotal(res.data.total)
        }
      } catch {
        if (!cancelled) toast.error('Failed to load clips')
      } finally {
        if (!cancelled) setAllMomentsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [viewMode, selectedProjectId, readyCount])

  // ── Fetch existing themes (clusters) ──
  useEffect(() => {
    if (viewMode !== 'themes' || !selectedProjectId) return
    let cancelled = false
    const load = async () => {
      setThemesLoading(true)
      try {
        const res = await apiClient.get('/clusters/', {
          params: { project_ids: selectedProjectId },
        })
        if (!cancelled) setThemes(res.data.clusters)
      } catch {
        if (!cancelled) toast.error('Failed to load themes')
      } finally {
        if (!cancelled) setThemesLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [viewMode, selectedProjectId])

  // ── Poll while any asset is ingesting ──
  useEffect(() => {
    if (ingestingCount === 0) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
      return
    }

    const poll = async () => {
      const ingesting = videoAssets.filter(a => getEffectiveStatus(a) === 'ingesting')
      if (ingesting.length === 0) return

      const results = await Promise.allSettled(
        ingesting.map(a => apiClient.get(`/ingest/${a.id}/status`)),
      )

      setStatuses(prev => {
        const next = { ...prev }
        results.forEach((r, i) => {
          if (r.status === 'fulfilled') {
            const data = r.value.data
            next[ingesting[i].id] = data
            // Sync asset status when backend disagrees with local state
            if (data.status !== 'ingesting') {
              setAssets(as => as.map(a =>
                a.id === ingesting[i].id ? { ...a, status: data.status } : a,
              ))
            }
          }
        })
        return next
      })
    }

    pollRef.current = setInterval(poll, 3000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [ingestingCount, videoAssets, getEffectiveStatus])

  // ── Actions ──
  const handleAnalyze = useCallback(async (assetId: number) => {
    try {
      await apiClient.post(`/ingest/${assetId}/start`)
      setAssets(prev => prev.map(a => a.id === assetId ? { ...a, status: 'ingesting' } : a))
      setStatuses(prev => ({
        ...prev,
        [assetId]: { status: 'ingesting', step: 'queued', progress: 0, moments_count: 0 },
      }))
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to start analysis'))
    }
  }, [])

  const handleRetry = useCallback(async (assetId: number) => {
    try {
      await apiClient.post(`/ingest/${assetId}/retry`)
      setAssets(prev => prev.map(a => a.id === assetId ? { ...a, status: 'ingesting' } : a))
      setStatuses(prev => ({
        ...prev,
        [assetId]: { status: 'ingesting', step: 'queued', progress: 0, moments_count: 0 },
      }))
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to retry'))
    }
  }, [])

  // Permanently remove an asset. Cascades through moments and any
  // draft references. Gate on a confirm because this is irreversible.
  const handleDeleteAsset = useCallback(async (asset: Asset) => {
    const ok = window.confirm(
      `Delete "${asset.original_filename}"? This permanently removes the video, its clips, and any drafts that reference them. Cannot be undone.`
    )
    if (!ok) return
    try {
      await assetApi.delete(asset.id)
      setAssets(prev => prev.filter(a => a.id !== asset.id))
      setStatuses(prev => {
        const next = { ...prev }
        delete next[asset.id]
        return next
      })
      toast.success(`Deleted ${asset.original_filename}`)
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to delete asset'))
    }
  }, [])

  // Re-run ingest on an already-ready asset. Destructive — backend deletes
  // existing moments — so gate on a confirm to prevent accidental loss.
  const handleReanalyze = useCallback((assetId: number) => {
    const ok = window.confirm(
      'Re-analyze this video? Existing shots and transcript will be replaced.'
    )
    if (!ok) return
    handleRetry(assetId)
  }, [handleRetry])

  const toggleExpand = useCallback(async (assetId: number) => {
    if (expandedAssetId === assetId) {
      setExpandedAssetId(null)
      return
    }
    setExpandedAssetId(assetId)
    if (!assetMoments[assetId]) {
      setMomentsLoading(true)
      try {
        const res = await apiClient.get('/moments/', { params: { asset_id: assetId, limit: 200 } })
        setAssetMoments(prev => ({ ...prev, [assetId]: res.data.moments }))
      } catch {
        toast.error('Failed to load clips')
      } finally {
        setMomentsLoading(false)
      }
    }
  }, [expandedAssetId, assetMoments])

  const handleAnalyzeAll = useCallback(async () => {
    if (unanalyzedAssets.length === 0) return
    const ids = unanalyzedAssets.map(a => a.id)
    toast.success(`Starting analysis for ${ids.length} video(s)...`)

    setAssets(prev => prev.map(a =>
      ids.includes(a.id) ? { ...a, status: 'ingesting' } : a,
    ))
    setStatuses(prev => {
      const next = { ...prev }
      ids.forEach(id => {
        next[id] = { status: 'ingesting', step: 'queued', progress: 0, moments_count: 0 }
      })
      return next
    })

    const results = await Promise.allSettled(
      ids.map(id => apiClient.post(`/ingest/${id}/start`)),
    )
    const failed = results.filter(r => r.status === 'rejected').length
    if (failed > 0) toast.error(`${failed} asset(s) failed to start`)
  }, [unanalyzedAssets])

  const handleGenerateThemes = useCallback(async () => {
    if (!selectedProjectId) return
    setThemesGenerating(true)
    try {
      const res = await apiClient.post('/clusters/auto-generate', null, {
        params: { project_id: selectedProjectId },
      })
      setThemes(res.data.clusters)
      toast.success(`Generated ${res.data.clusters.length} themes`)
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to generate themes'))
    } finally {
      setThemesGenerating(false)
    }
  }, [selectedProjectId])

  const handleEvaluate = useCallback(async () => {
    if (!selectedProjectId) return
    setEvaluating(true)
    try {
      const res = await apiClient.post('/moments/evaluate', null, {
        params: { project_id: selectedProjectId },
      })
      setScoredMoments(res.data.moments)
      const dc = res.data.discarded_count
      toast.success(`Evaluated ${res.data.total} clips — ${dc} discarded`)
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to evaluate clips'))
    } finally {
      setEvaluating(false)
    }
  }, [selectedProjectId])

  const discardedMoments = useMemo(
    () => scoredMoments.filter(m => m.discarded),
    [scoredMoments],
  )
  const keptMoments = useMemo(
    () => scoredMoments.filter(m => !m.discarded),
    [scoredMoments],
  )

  const handleRelocateVideo = useCallback(async (targetProjectId: string) => {
    if (!relocateVideoAssetId) return
    setRelocating(true)
    try {
      await apiClient.post(`/assets/${relocateVideoAssetId}/relocate`, {
        target_project_id: targetProjectId,
      })
      setAssets(prev => prev.filter(a => a.id !== relocateVideoAssetId))
      setAssetMoments(prev => {
        const next = { ...prev }
        delete next[relocateVideoAssetId]
        return next
      })
      if (expandedAssetId === relocateVideoAssetId) setExpandedAssetId(null)
      toast.success('Video moved to another workspace')
      setRelocateVideoAssetId(null)
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to move video'))
    } finally {
      setRelocating(false)
    }
  }, [relocateVideoAssetId, expandedAssetId])

  const handleRelocateClip = useCallback(async (targetAssetId: number) => {
    if (!relocateClipMomentId) return
    setRelocating(true)
    try {
      await apiClient.post(`/moments/${relocateClipMomentId}/relocate`, {
        target_asset_id: targetAssetId,
      })
      setAllMoments(prev => prev.filter(m => m.id !== relocateClipMomentId))
      setAllMomentsTotal(prev => prev - 1)
      toast.success('Clip moved to another video')
      setRelocateClipMomentId(null)
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to move clip'))
    } finally {
      setRelocating(false)
    }
  }, [relocateClipMomentId])

  const hasContent = selectedProjectId && !assetsLoading && videoAssets.length > 0

  // ── Render ──
  return (
    <div className="rq-page">
      {/* Header */}
      <div className="mb-4">
        <h1 className="rq-title">Analysis</h1>
        <p className="rq-subtitle">
          Detect shots and extract signals from your video assets before creating an AI draft.
        </p>
      </div>

      {/* Search */}
      <div className="mb-6 max-w-md">
        <GlobalSearch inline />
      </div>

      {/* Workspace selector + Analyze All */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        {/* Workspace picker button */}
        <div className="relative">
          <button
            onClick={() => setShowProjectPicker(!showProjectPicker)}
            disabled={projectsLoading}
            className="flex items-center gap-2 px-4 py-2.5 rq-card rq-card-hover min-w-[200px]"
          >
            <Layers className="w-4 h-4 text-[var(--rq-text-soft)]" />
            <span className="flex-1 text-left truncate text-[var(--rq-text)] text-sm">
              {projectsLoading
                ? 'Loading...'
                : selectedProjectName || 'Select workspace'
              }
            </span>
            <ChevronDown className={`w-4 h-4 text-[var(--rq-text-soft)] transition-transform ${showProjectPicker ? 'rotate-180' : ''}`} />
          </button>

          {showProjectPicker && (
            <div className="absolute z-20 mt-1 w-full max-w-sm rq-card shadow-xl overflow-hidden">
              {projects.map(p => (
                <button
                  key={p.id}
                  onClick={() => {
                    setSelectedProjectId(p.id)
                    setShowProjectPicker(false)
                  }}
                  className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center gap-3 ${
                    selectedProjectId === p.id
                      ? 'bg-white/10 text-[var(--rq-text)]'
                      : 'text-[var(--rq-text-soft)] hover:bg-white/5 hover:text-[var(--rq-text)]'
                  }`}
                >
                  <span className="truncate flex-1">{p.name}</span>
                </button>
              ))}
              {projects.length === 0 && (
                <div className="px-4 py-3 text-sm text-[var(--rq-text-soft)]">No workspaces yet</div>
              )}
            </div>
          )}
        </div>

        {unanalyzedAssets.length > 0 && (
          <button
            onClick={handleAnalyzeAll}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 transition-colors whitespace-nowrap"
          >
            <Cpu className="w-4 h-4" />
            Analyze All ({unanalyzedAssets.length})
          </button>
        )}

        {/* Spacer + Create AI Draft on the right */}
        {readyCount > 0 && (
          <button
            onClick={() => navigate(`/create-ai-draft?project_id=${selectedProjectId}`)}
            className="sm:ml-auto rq-btn-primary px-4 py-2.5 text-sm"
          >
            Create AI Draft
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Tab bar */}
      {hasContent && (
        <div className="flex gap-1 mb-6 border-b border-white/10">
          <button
            onClick={() => setViewMode('by-video')}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              viewMode === 'by-video'
                ? 'text-[var(--rq-text)] border-[var(--btn)]'
                : 'text-[var(--rq-text-soft)] border-transparent hover:text-[var(--rq-text)]'
            }`}
          >
            <Layers className="w-4 h-4" />
            By Video
            <span className="text-xs opacity-60">({videoAssets.length})</span>
          </button>
          <button
            onClick={() => setViewMode('all-clips')}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              viewMode === 'all-clips'
                ? 'text-[var(--rq-text)] border-[var(--btn)]'
                : 'text-[var(--rq-text-soft)] border-transparent hover:text-[var(--rq-text)]'
            }`}
          >
            <LayoutList className="w-4 h-4" />
            All Clips
            {allMomentsTotal > 0 && <span className="text-xs opacity-60">({allMomentsTotal})</span>}
          </button>
          <button
            onClick={() => setViewMode('themes')}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              viewMode === 'themes'
                ? 'text-[var(--rq-text)] border-[var(--btn)]'
                : 'text-[var(--rq-text-soft)] border-transparent hover:text-[var(--rq-text)]'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            Themes
            {themes.length > 0 && <span className="text-xs opacity-60">({themes.length})</span>}
          </button>
          <button
            onClick={() => setViewMode('discarded')}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              viewMode === 'discarded'
                ? 'text-[var(--rq-text)] border-[var(--btn)]'
                : 'text-[var(--rq-text-soft)] border-transparent hover:text-[var(--rq-text)]'
            }`}
          >
            <Trash2 className="w-4 h-4" />
            Discarded
            {discardedMoments.length > 0 && <span className="text-xs opacity-60">({discardedMoments.length})</span>}
          </button>
        </div>
      )}

      {/* Content */}
      <div>
        {!selectedProjectId ? (
          <div className="text-center py-16">
            <Layers className="w-16 h-16 text-[var(--rq-text-soft)] mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-[var(--rq-text)] mb-2">Select a workspace</h2>
            <p className="text-[var(--rq-text-soft)]">Choose a workspace above to browse video assets</p>
          </div>
        ) : assetsLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-[var(--rq-text-soft)] animate-spin" />
          </div>
        ) : videoAssets.length === 0 ? (
          <div className="text-center py-16">
            <Film className="w-16 h-16 text-[var(--rq-text-soft)] mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-[var(--rq-text)] mb-2">No video assets</h2>
            <p className="text-[var(--rq-text-soft)]">Upload videos to your workspace, then come back to analyze them.</p>
          </div>
        ) : viewMode === 'all-clips' ? (
          /* ═══════════════════════════════════════════
             All Clips view — matches Collections design
             ═══════════════════════════════════════════ */
          <div>
            {allMomentsLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 text-[var(--rq-text-soft)] animate-spin" />
              </div>
            ) : allMoments.length === 0 ? (
              <div className="text-center py-16">
                <Film className="w-16 h-16 text-[var(--rq-text-soft)] mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-[var(--rq-text)] mb-2">No clips yet</h2>
                <p className="text-[var(--rq-text-soft)] max-w-md mx-auto">
                  Analyze your videos to detect shots and generate clips.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Stats bar + layout toggle */}
                <div className="flex items-center gap-4 text-sm text-[var(--rq-text-soft)]">
                  <span className="flex items-center gap-1.5">
                    <Play className="w-3.5 h-3.5" />
                    {allMoments.length} / {allMomentsTotal} clips
                  </span>
                  <span className="text-[var(--rq-text-soft)]">&middot;</span>
                  <span className="flex items-center gap-1.5">
                    <Hourglass className="w-3.5 h-3.5" />
                    {formatTotalDuration(totalClipDuration)}
                  </span>
                  {distinctVideoCount > 0 && (
                    <>
                      <span className="text-[var(--rq-text-soft)]">&middot;</span>
                      <span>{distinctVideoCount} video{distinctVideoCount !== 1 ? 's' : ''}</span>
                    </>
                  )}
                  <div className="ml-auto">
                    <ViewToggle mode={layoutMode} onChange={setLayoutMode} />
                  </div>
                </div>

                {layoutMode === 'list' ? (
                  /* ── List view ── */
                  <div>
                    {/* Header row */}
                    <div className="hidden sm:grid grid-cols-[1fr_140px_140px_40px] items-center gap-4 px-2 py-3 border-b border-white/10 text-sm font-medium text-[var(--rq-text-soft)]">
                      <span>Clip title</span>
                      <span className="text-right">Clip duration</span>
                      <span className="text-right">Date uploaded</span>
                      <span></span>
                    </div>

                    {/* Rows */}
                    {allMoments.map(moment => {
                      const clipDuration = moment.start_time !== null && moment.end_time !== null
                        ? moment.end_time - moment.start_time
                        : null
                      return (
                        <div
                          key={moment.id}
                          className="grid grid-cols-1 sm:grid-cols-[1fr_140px_140px_40px] items-center gap-2 sm:gap-4 px-2 py-4 border-b border-white/5 hover:bg-white/[0.03] transition-colors"
                        >
                          {/* Thumbnail + title */}
                          <div className="flex items-center gap-4 min-w-0">
                            <div
                              onClick={() => openFromMoment(moment)}
                              className="w-24 h-14 rounded-lg bg-black/40 overflow-hidden flex-shrink-0 relative group/thumb cursor-pointer"
                            >
                              {moment.thumbnail_url ? (
                                <img
                                  src={moment.thumbnail_url}
                                  alt={moment.title || 'Clip thumbnail'}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Film className="w-6 h-6 text-[var(--rq-text-soft)]" />
                                </div>
                              )}
                              <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover/thumb:opacity-100 transition-opacity">
                                <Play className="w-4 h-4 text-white" />
                              </div>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-[var(--rq-text)] truncate">
                                {moment.title || `Shot ${moment.scene_index + 1}`}
                              </p>
                              {(moment.visual_description || moment.audio_description) && (
                                <p className="text-xs text-[var(--rq-text-soft)] line-clamp-2 mt-0.5 leading-relaxed">
                                  {[moment.visual_description, moment.audio_description].filter(Boolean).join(' — ')}
                                </p>
                              )}
                              {moment.asset_filename && (
                                <p className="text-[11px] text-[var(--rq-text-soft)]/70 truncate mt-1">{moment.asset_filename}</p>
                              )}
                            </div>
                          </div>

                          {/* Duration + Date — stacked on mobile, columns on desktop */}
                          <div className="sm:hidden flex items-center gap-4 pl-28 text-xs text-[var(--rq-text-soft)]">
                            <span>{formatDurationHMS(clipDuration)}</span>
                            <span>&middot;</span>
                            <span>{formatDate(moment.created_time)}</span>
                          </div>
                          <span className="hidden sm:block text-sm text-[var(--rq-text-soft)] text-right tabular-nums">
                            {formatDurationHMS(clipDuration)}
                          </span>
                          <span className="hidden sm:block text-sm text-[var(--rq-text-soft)] text-right tabular-nums">
                            {formatDate(moment.created_time)}
                          </span>
                          <div className="hidden sm:flex items-center justify-end">
                            <button
                              onClick={() => setRelocateClipMomentId(moment.id)}
                              className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-1 transition-colors"
                              title="Move to another video"
                            >
                              <ArrowRightLeft className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  /* ── Grid view ── */
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {allMoments.map(moment => {
                      const clipDuration = moment.start_time !== null && moment.end_time !== null
                        ? moment.end_time - moment.start_time
                        : null
                      return (
                        <div
                          key={moment.id}
                          onClick={() => openFromMoment(moment)}
                          className="group rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-white/15 transition-colors overflow-hidden cursor-pointer"
                        >
                          {/* Thumbnail */}
                          <div className="relative aspect-video bg-black/40">
                            {moment.thumbnail_url ? (
                              <img
                                src={moment.thumbnail_url}
                                alt={moment.title || 'Clip thumbnail'}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Film className="w-8 h-8 text-[var(--rq-text-soft)]" />
                              </div>
                            )}
                            {/* Duration badge */}
                            {clipDuration !== null && (
                              <span className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-[10px] font-medium px-1.5 py-0.5 rounded tabular-nums">
                                {formatDuration(clipDuration)}
                              </span>
                            )}
                            {/* Source badge */}
                            {moment.asset_filename && (
                              <span className="absolute top-1.5 right-1.5 bg-black/60 text-white/80 text-[9px] px-1.5 py-0.5 rounded truncate max-w-[80%]">
                                {moment.asset_filename}
                              </span>
                            )}
                            {/* Play icon on hover */}
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Play className="w-8 h-8 text-white" />
                            </div>
                            {/* Relocate on hover */}
                            <button
                              onClick={e => { e.stopPropagation(); setRelocateClipMomentId(moment.id) }}
                              className="absolute top-1.5 left-1.5 opacity-0 group-hover:opacity-100 bg-black/60 hover:bg-black/80 text-white/80 hover:text-white p-1 rounded transition-all"
                              title="Move to another video"
                            >
                              <ArrowRightLeft className="w-3 h-3" />
                            </button>
                          </div>
                          {/* Info */}
                          <div className="px-3 py-2.5 space-y-1">
                            <p className="text-xs font-medium text-[var(--rq-text)] line-clamp-1">
                              {moment.title || `Shot ${moment.scene_index + 1}`}
                            </p>
                            {(moment.visual_description || moment.audio_description) && (
                              <p className="text-[11px] text-[var(--rq-text-soft)] line-clamp-3 leading-relaxed">
                                {[moment.visual_description, moment.audio_description].filter(Boolean).join(' — ')}
                              </p>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : viewMode === 'by-video' ? (
          /* ═══════════════════════════════════════════
             By Video view — per-asset expandable rows
             ═══════════════════════════════════════════ */
          <div className="space-y-4">
            {/* Summary */}
            <div className="flex items-center gap-4 text-sm text-[var(--rq-text-soft)]">
              <span>{readyCount} of {videoAssets.length} video{videoAssets.length !== 1 ? 's' : ''} analyzed</span>
              {ingestingCount > 0 && (
                <>
                  <span>&middot;</span>
                  <span>{ingestingCount} in progress</span>
                </>
              )}
              <div className="ml-auto">
                <ViewToggle mode={layoutMode} onChange={setLayoutMode} />
              </div>
            </div>

            {layoutMode === 'list' ? (
              <div className="space-y-2">
                {videoAssets.map(asset => {
                  const effectiveStatus = getEffectiveStatus(asset)
                  const st = statuses[asset.id]
                  const step = st?.step || ''
                  const progress = st?.progress || 0
                  const momentsCount = st?.moments_count || 0
                  const isExpanded = expandedAssetId === asset.id
                  const isReady = effectiveStatus === 'ready'
                  const clips = assetMoments[asset.id]

                  return (
                    <div key={asset.id}>
                      <div
                        onClick={isReady ? () => toggleExpand(asset.id) : undefined}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border transition-colors ${
                          isExpanded
                            ? 'border-white/15 rounded-b-none'
                            : 'border-white/[0.06] hover:border-white/10'
                        } ${isReady ? 'cursor-pointer' : ''}`}
                      >
                        {/* Expand indicator */}
                        {isReady ? (
                          <ChevronDown className={`w-4 h-4 text-[var(--rq-text-soft)] flex-shrink-0 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                        ) : (
                          <div className="w-4 flex-shrink-0" />
                        )}

                        {/* Thumbnail */}
                        <div
                          onClick={e => { e.stopPropagation(); openFromAsset({ assetId: asset.id, fileType: asset.file_type, title: asset.original_filename, fallbackImageUrl: asset.thumbnail_url }) }}
                          className="w-16 h-10 rounded-lg bg-black/30 overflow-hidden flex-shrink-0 flex items-center justify-center relative group/thumb cursor-pointer"
                        >
                          {asset.thumbnail_url ? (
                            <img src={asset.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <Film className="w-4 h-4 text-[var(--rq-text-soft)]" />
                          )}
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover/thumb:opacity-100 transition-opacity">
                            <Play className="w-3.5 h-3.5 text-white" />
                          </div>
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[var(--rq-text)] truncate">{asset.original_filename}</p>
                          <p className="text-[10px] text-[var(--rq-text-soft)] mt-0.5">
                            {effectiveStatus === 'uploaded' && 'Not analyzed'}
                            {effectiveStatus === 'ingesting' && (STEP_LABELS[step] || step.replace(/_/g, ' ') || 'Processing...')}
                            {effectiveStatus === 'ready' && `${momentsCount} shot${momentsCount !== 1 ? 's' : ''} detected`}
                            {effectiveStatus === 'failed' && (st?.error ? st.error.slice(0, 60) : 'Analysis failed')}
                          </p>
                        </div>

                        {/* Duration */}
                        <span className="hidden sm:block text-xs text-[var(--rq-text-soft)] tabular-nums w-12 text-right flex-shrink-0">
                          {formatDuration(asset.duration)}
                        </span>

                        {/* Relocate */}
                        <button
                          onClick={e => { e.stopPropagation(); setRelocateVideoAssetId(asset.id) }}
                          className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-1 transition-colors flex-shrink-0"
                          title="Move to another workspace"
                        >
                          <ArrowRightLeft className="w-3.5 h-3.5" />
                        </button>

                        {/* Delete */}
                        <button
                          onClick={e => { e.stopPropagation(); handleDeleteAsset(asset) }}
                          className="text-[var(--rq-text-soft)] hover:text-red-300 p-1 transition-colors flex-shrink-0"
                          title="Delete video"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>

                        {/* Status / Action */}
                        <div className="w-28 sm:w-32 flex-shrink-0 flex items-center justify-end">
                          {effectiveStatus === 'uploaded' && (
                            <button
                              onClick={e => { e.stopPropagation(); handleAnalyze(asset.id) }}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 transition-colors"
                            >
                              <Cpu className="w-3.5 h-3.5" />
                              Analyze
                            </button>
                          )}

                          {effectiveStatus === 'ingesting' && (
                            <div className="w-full space-y-1">
                              <div className="flex justify-between text-[9px]">
                                <span className="text-amber-300">{progress}%</span>
                              </div>
                              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-amber-400 rounded-full transition-all duration-500"
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                            </div>
                          )}

                          {effectiveStatus === 'ready' && (
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1.5 text-xs text-emerald-400">
                                <CheckCircle className="w-3.5 h-3.5" />
                                Ready
                              </div>
                              <button
                                onClick={e => { e.stopPropagation(); handleReanalyze(asset.id) }}
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] hover:bg-white/5 transition-colors"
                                title="Re-analyze (replaces existing shots)"
                              >
                                <RotateCcw className="w-3 h-3" />
                                Re-analyze
                              </button>
                            </div>
                          )}

                          {effectiveStatus === 'failed' && (
                            <button
                              onClick={e => { e.stopPropagation(); handleRetry(asset.id) }}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-red-400 hover:bg-red-400/10 transition-colors"
                            >
                              <RotateCcw className="w-3 h-3" />
                              Retry
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Expanded clips — respects the page-level layoutMode */}
                      {isExpanded && (
                        <div className="border border-t-0 border-white/15 rounded-b-xl bg-white/[0.02] overflow-hidden">
                          {momentsLoading && !clips ? (
                            <div className="flex items-center justify-center py-6">
                              <Loader2 className="w-4 h-4 text-[var(--rq-text-soft)] animate-spin" />
                            </div>
                          ) : clips && clips.length > 0 ? (
                            layoutMode === 'list' ? (
                              <div className="divide-y divide-white/5">
                                {clips.map(moment => {
                                  const clipDuration = moment.start_time !== null && moment.end_time !== null
                                    ? moment.end_time - moment.start_time
                                    : null
                                  return (
                                    <div
                                      key={moment.id}
                                      onClick={() => openFromMoment(moment)}
                                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.03] transition-colors cursor-pointer"
                                    >
                                      <div className="w-20 h-12 rounded-lg bg-black/40 overflow-hidden flex-shrink-0 relative group/thumb">
                                        {moment.thumbnail_url ? (
                                          <img src={moment.thumbnail_url} alt={moment.title || ''} className="w-full h-full object-cover" loading="lazy" />
                                        ) : (
                                          <div className="w-full h-full flex items-center justify-center">
                                            <Film className="w-4 h-4 text-[var(--rq-text-soft)]" />
                                          </div>
                                        )}
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover/thumb:opacity-100 transition-opacity">
                                          <Play className="w-3.5 h-3.5 text-white" />
                                        </div>
                                      </div>

                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm text-[var(--rq-text)] truncate">
                                          {moment.visual_description || moment.title || `Shot ${moment.scene_index + 1}`}
                                        </p>
                                        {moment.start_time !== null && (
                                          <p className="text-[10px] text-[var(--rq-text-soft)] mt-0.5">
                                            {formatDurationHMS(moment.start_time)} – {formatDurationHMS(moment.end_time)}
                                          </p>
                                        )}
                                      </div>

                                      {clipDuration !== null && (
                                        <span className="text-xs text-[var(--rq-text-soft)] tabular-nums flex-shrink-0">
                                          {formatDurationHMS(clipDuration)}
                                        </span>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            ) : (
                              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 p-3">
                                {clips.map(moment => {
                                  const clipDuration = moment.start_time !== null && moment.end_time !== null
                                    ? moment.end_time - moment.start_time
                                    : null
                                  return (
                                    <div
                                      key={moment.id}
                                      onClick={() => openFromMoment(moment)}
                                      className="group/clip rounded-lg bg-white/[0.03] border border-white/[0.08] overflow-hidden cursor-pointer hover:border-white/20 transition-colors"
                                    >
                                      <div className="relative aspect-video bg-black/40">
                                        {moment.thumbnail_url ? (
                                          <img src={moment.thumbnail_url} alt={moment.title || ''} className="w-full h-full object-cover" loading="lazy" />
                                        ) : (
                                          <div className="w-full h-full flex items-center justify-center">
                                            <Film className="w-5 h-5 text-[var(--rq-text-soft)]" />
                                          </div>
                                        )}
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover/clip:opacity-100 transition-opacity">
                                          <Play className="w-6 h-6 text-white" />
                                        </div>
                                        {clipDuration !== null && (
                                          <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded tabular-nums">
                                            {formatDurationHMS(clipDuration)}
                                          </span>
                                        )}
                                      </div>
                                      <div className="p-2 space-y-0.5">
                                        <p className="text-xs text-[var(--rq-text)] truncate">
                                          {moment.title || `Shot ${moment.scene_index + 1}`}
                                        </p>
                                        {moment.start_time !== null && (
                                          <p className="text-[10px] text-[var(--rq-text-soft)] tabular-nums">
                                            {formatDurationHMS(moment.start_time)} – {formatDurationHMS(moment.end_time)}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            )
                          ) : (
                            <div className="py-6 text-center text-xs text-[var(--rq-text-soft)]">
                              No clips found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {videoAssets.map(asset => {
                  const effectiveStatus = getEffectiveStatus(asset)
                  const st = statuses[asset.id]
                  const progress = st?.progress || 0
                  const momentsCount = st?.moments_count || 0
                  return (
                    <div key={asset.id} className="group rounded-xl bg-white/[0.03] border border-white/[0.08] overflow-hidden">
                      <div
                        onClick={() => openFromAsset({ assetId: asset.id, fileType: asset.file_type, title: asset.original_filename, fallbackImageUrl: asset.thumbnail_url })}
                        className="relative aspect-video bg-black/40 cursor-pointer"
                      >
                        {asset.thumbnail_url ? (
                          <img src={asset.thumbnail_url} alt={asset.original_filename} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Film className="w-8 h-8 text-[var(--rq-text-soft)]" />
                          </div>
                        )}
                        {effectiveStatus === 'ready' && (
                          <span className="absolute bottom-1.5 right-1.5 bg-emerald-500/20 text-emerald-300 text-[10px] px-1.5 py-0.5 rounded">
                            {momentsCount} clips
                          </span>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Play className="w-8 h-8 text-white" />
                        </div>
                      </div>
                      <div className="p-3 space-y-2">
                        <p className="text-sm text-[var(--rq-text)] truncate">{asset.original_filename}</p>
                        <p className="text-[11px] text-[var(--rq-text-soft)]">
                          {effectiveStatus === 'uploaded' && 'Not analyzed'}
                          {effectiveStatus === 'ingesting' && 'Processing...'}
                          {effectiveStatus === 'ready' && `${momentsCount} shot${momentsCount !== 1 ? 's' : ''} detected`}
                          {effectiveStatus === 'failed' && 'Analysis failed'}
                        </p>
                        {effectiveStatus === 'ingesting' && (
                          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full bg-amber-400 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-[var(--rq-text-soft)] tabular-nums">{formatDuration(asset.duration)}</span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={e => { e.stopPropagation(); setRelocateVideoAssetId(asset.id) }}
                              className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-1 transition-colors"
                              title="Move to another workspace"
                            >
                              <ArrowRightLeft className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={e => { e.stopPropagation(); handleDeleteAsset(asset) }}
                              className="text-[var(--rq-text-soft)] hover:text-red-300 p-1 transition-colors"
                              title="Delete video"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                            {effectiveStatus === 'uploaded' && (
                              <button
                                onClick={e => { e.stopPropagation(); handleAnalyze(asset.id) }}
                                className="text-xs px-2 py-1 rounded bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 transition-colors"
                              >
                                Analyze
                              </button>
                            )}
                            {effectiveStatus === 'ready' && (
                              <button
                                onClick={e => { e.stopPropagation(); handleReanalyze(asset.id) }}
                                className="text-xs px-2 py-1 rounded text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] hover:bg-white/10 transition-colors flex items-center gap-1"
                                title="Re-analyze (replaces existing shots)"
                              >
                                <RotateCcw className="w-3 h-3" />
                                Re-analyze
                              </button>
                            )}
                            {effectiveStatus === 'failed' && (
                              <button
                                onClick={e => { e.stopPropagation(); handleRetry(asset.id) }}
                                className="text-xs px-2 py-1 rounded bg-red-500/15 text-red-300 hover:bg-red-500/25 transition-colors"
                              >
                                Retry
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        ) : viewMode === 'themes' ? (
          /* ═══════════════════════════════════════════
             Themes view — auto-generated clip clusters
             ═══════════════════════════════════════════ */
          <div className="space-y-4">
            {/* Generate button + summary */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                onClick={handleGenerateThemes}
                disabled={themesGenerating || readyCount === 0}
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-purple-500/15 text-purple-300 hover:bg-purple-500/25 transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {themesGenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                {themesGenerating ? 'Generating...' : themes.length > 0 ? 'Regenerate Themes' : 'Generate Themes'}
              </button>
              {themes.length > 0 && (
                <span className="text-sm text-[var(--rq-text-soft)]">
                  {themes.length} theme{themes.length !== 1 ? 's' : ''} · {themes.reduce((s, t) => s + t.moments_count, 0)} clips
                </span>
              )}
              {themes.length > 0 && (
                <div className="sm:ml-auto">
                  <ViewToggle mode={layoutMode} onChange={setLayoutMode} />
                </div>
              )}
            </div>

            {themesLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 text-[var(--rq-text-soft)] animate-spin" />
              </div>
            ) : themes.length === 0 ? (
              <div className="text-center py-16">
                <Sparkles className="w-16 h-16 text-[var(--rq-text-soft)] mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-[var(--rq-text)] mb-2">No themes yet</h2>
                <p className="text-[var(--rq-text-soft)] max-w-md mx-auto">
                  {readyCount > 0
                    ? 'Click "Generate Themes" to automatically group your clips by theme using AI.'
                    : 'Analyze your videos first, then generate themes to group clips.'}
                </p>
              </div>
            ) : layoutMode === 'list' ? (
              <div className="space-y-2">
                {themes.map(theme => {
                  const isExpanded = expandedThemeId === theme.id
                  return (
                    <div key={theme.id}>
                      <div
                        onClick={() => setExpandedThemeId(isExpanded ? null : theme.id)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border transition-colors cursor-pointer ${
                          isExpanded
                            ? 'border-white/15 rounded-b-none'
                            : 'border-white/[0.06] hover:border-white/10'
                        }`}
                      >
                        <ChevronDown className={`w-4 h-4 text-[var(--rq-text-soft)] flex-shrink-0 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />

                        {/* Theme thumbnail — first clip's thumbnail */}
                        <div className="w-16 h-10 rounded-lg bg-black/30 overflow-hidden flex-shrink-0 flex items-center justify-center">
                          {theme.moments[0]?.thumbnail_url ? (
                            <img src={theme.moments[0].thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <Sparkles className="w-4 h-4 text-[var(--rq-text-soft)]" />
                          )}
                        </div>

                        {/* Theme info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[var(--rq-text)] font-medium truncate">{theme.name}</p>
                          <p className="text-[10px] text-[var(--rq-text-soft)] mt-0.5">
                            {theme.moments_count} clip{theme.moments_count !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>

                      {/* Expanded clips */}
                      {isExpanded && (
                        <div className="border border-t-0 border-white/15 rounded-b-xl bg-white/[0.02] overflow-hidden">
                          {theme.moments.length > 0 ? (
                            <div className="divide-y divide-white/5">
                              {theme.moments.map(moment => {
                                const clipDuration = moment.start_time !== null && moment.end_time !== null
                                  ? moment.end_time - moment.start_time
                                  : null
                                return (
                                  <div
                                    key={moment.id}
                                    onClick={() => openFromMoment(moment)}
                                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.03] transition-colors cursor-pointer"
                                  >
                                    <div className="w-20 h-12 rounded-lg bg-black/40 overflow-hidden flex-shrink-0 relative group/thumb">
                                      {moment.thumbnail_url ? (
                                        <img src={moment.thumbnail_url} alt={moment.title || ''} className="w-full h-full object-cover" loading="lazy" />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                          <Film className="w-4 h-4 text-[var(--rq-text-soft)]" />
                                        </div>
                                      )}
                                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover/thumb:opacity-100 transition-opacity">
                                        <Play className="w-3.5 h-3.5 text-white" />
                                      </div>
                                    </div>

                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm text-[var(--rq-text)] truncate">
                                        {moment.title || `Shot ${moment.scene_index + 1}`}
                                      </p>
                                      {moment.asset_filename && (
                                        <p className="text-[10px] text-[var(--rq-text-soft)] mt-0.5 truncate">{moment.asset_filename}</p>
                                      )}
                                    </div>

                                    {clipDuration !== null && (
                                      <span className="text-xs text-[var(--rq-text-soft)] tabular-nums flex-shrink-0">
                                        {formatDurationHMS(clipDuration)}
                                      </span>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          ) : (
                            <div className="py-6 text-center text-xs text-[var(--rq-text-soft)]">
                              No clips in this theme
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {themes.map(theme => (
                  <div
                    key={theme.id}
                    onClick={() => theme.moments[0] && openFromMoment(theme.moments[0])}
                    className="group rounded-xl bg-white/[0.03] border border-white/[0.08] overflow-hidden cursor-pointer"
                  >
                    <div className="relative aspect-video bg-black/40">
                      {theme.moments[0]?.thumbnail_url ? (
                        <img src={theme.moments[0].thumbnail_url} alt={theme.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Sparkles className="w-8 h-8 text-[var(--rq-text-soft)]" />
                        </div>
                      )}
                      <span className="absolute bottom-1.5 right-1.5 bg-purple-500/20 text-purple-300 text-[10px] px-1.5 py-0.5 rounded">
                        {theme.moments_count} clips
                      </span>
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Play className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="text-sm text-[var(--rq-text)] font-medium truncate">{theme.name}</p>
                      {theme.description && (
                        <p className="text-[11px] text-[var(--rq-text-soft)] mt-1 line-clamp-2">{theme.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* ═══════════════════════════════════════════
             Discarded view — low-scoring clips
             ═══════════════════════════════════════════ */
          <div className="space-y-4">
            {/* Evaluate button + summary */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                onClick={handleEvaluate}
                disabled={evaluating || readyCount === 0}
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-red-500/15 text-red-300 hover:bg-red-500/25 transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {evaluating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                {evaluating ? 'Evaluating...' : scoredMoments.length > 0 ? 'Re-evaluate' : 'Evaluate Clips'}
              </button>
              {scoredMoments.length > 0 && (
                <span className="text-sm text-[var(--rq-text-soft)]">
                  {discardedMoments.length} discarded · {keptMoments.length} kept · {scoredMoments.length} total
                </span>
              )}
            </div>

            {scoredMoments.length === 0 ? (
              <div className="text-center py-16">
                <Trash2 className="w-16 h-16 text-[var(--rq-text-soft)] mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-[var(--rq-text)] mb-2">No evaluation yet</h2>
                <p className="text-[var(--rq-text-soft)] max-w-md mx-auto">
                  {readyCount > 0
                    ? 'Click "Evaluate Clips" to score all clips by audio energy, motion, and speaker activity. Low-scoring clips will appear here.'
                    : 'Analyze your videos first, then evaluate to find low-value clips.'}
                </p>
              </div>
            ) : discardedMoments.length === 0 ? (
              <div className="text-center py-16">
                <CheckCircle className="w-16 h-16 text-emerald-400/50 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-[var(--rq-text)] mb-2">All clips look good</h2>
                <p className="text-[var(--rq-text-soft)] max-w-md mx-auto">
                  No clips scored below the threshold. All {scoredMoments.length} clips have sufficient editorial value.
                </p>
              </div>
            ) : (
              <div>
                {/* Header row */}
                <div className="hidden sm:grid grid-cols-[1fr_100px_100px] items-center gap-4 px-2 py-3 border-b border-white/10 text-sm font-medium text-[var(--rq-text-soft)]">
                  <span>Clip</span>
                  <span className="text-right">Score</span>
                  <span className="text-right">Duration</span>
                </div>

                {/* Rows */}
                {discardedMoments.map(moment => {
                  const clipDuration = moment.start_time !== null && moment.end_time !== null
                    ? moment.end_time - moment.start_time
                    : null
                  const score = moment.event_score ?? 0

                  return (
                    <div
                      key={moment.id}
                      className="grid grid-cols-1 sm:grid-cols-[1fr_100px_100px] items-center gap-2 sm:gap-4 px-2 py-4 border-b border-white/5 hover:bg-white/[0.03] transition-colors"
                    >
                      {/* Thumbnail + info */}
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-24 h-14 rounded-lg bg-black/40 overflow-hidden flex-shrink-0 relative">
                          {moment.thumbnail_url ? (
                            <img
                              src={moment.thumbnail_url}
                              alt={moment.title || 'Clip thumbnail'}
                              className="w-full h-full object-cover opacity-60"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Film className="w-6 h-6 text-[var(--rq-text-soft)]" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-[var(--rq-text)] truncate">
                            {moment.visual_description || moment.title || `Shot ${moment.scene_index + 1}`}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            {moment.asset_filename && (
                              <span className="text-[10px] text-[var(--rq-text-soft)] truncate">{moment.asset_filename}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Score + Duration — stacked on mobile */}
                      <div className="sm:hidden flex items-center gap-4 pl-28 text-xs text-[var(--rq-text-soft)]">
                        <span className="text-red-400">{Math.round(score * 100)}%</span>
                        <span>&middot;</span>
                        <span>{formatDurationHMS(clipDuration)}</span>
                      </div>
                      <div className="hidden sm:flex items-center justify-end gap-1.5">
                        <div className="w-12 h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-red-400/60 rounded-full"
                            style={{ width: `${Math.round(score * 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-red-400 tabular-nums w-8 text-right">{Math.round(score * 100)}%</span>
                      </div>
                      <span className="hidden sm:block text-sm text-[var(--rq-text-soft)] text-right tabular-nums">
                        {formatDurationHMS(clipDuration)}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════
          Relocate Video Modal — workspace picker
          ═══════════════════════════════════════════ */}
      {relocateVideoAssetId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setRelocateVideoAssetId(null)}>
          <div className="rq-card w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[var(--rq-text)]">Move Video to Workspace</h3>
              <button onClick={() => setRelocateVideoAssetId(null)} className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-[var(--rq-text-soft)] mb-4">
              Choose a workspace to move this video to. It will be removed from the current workspace.
            </p>
            <div className="max-h-72 overflow-y-auto space-y-1">
              {projects
                .filter(p => p.id !== selectedProjectId)
                .map(p => (
                  <button
                    key={p.id}
                    disabled={relocating}
                    onClick={() => handleRelocateVideo(p.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm rounded-lg transition-colors text-[var(--rq-text-soft)] hover:bg-white/5 hover:text-[var(--rq-text)] disabled:opacity-50"
                  >
                    <Layers className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate flex-1">{p.name}</span>
                    {relocating && <Loader2 className="w-4 h-4 animate-spin" />}
                  </button>
                ))}
              {projects.filter(p => p.id !== selectedProjectId).length === 0 && (
                <p className="text-sm text-[var(--rq-text-soft)] py-4 text-center">No other workspaces available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════
          Relocate Clip Modal — video picker
          ═══════════════════════════════════════════ */}
      {relocateClipMomentId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setRelocateClipMomentId(null)}>
          <div className="rq-card w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[var(--rq-text)]">Move Clip to Video</h3>
              <button onClick={() => setRelocateClipMomentId(null)} className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-[var(--rq-text-soft)] mb-4">
              Choose a video to move this clip to. It will be appended to the end of the target video's clip list.
            </p>
            <div className="max-h-72 overflow-y-auto space-y-1">
              {videoAssets
                .filter(a => {
                  const clip = allMoments.find(m => m.id === relocateClipMomentId)
                  return clip ? a.id !== clip.asset_id : true
                })
                .map(a => (
                  <button
                    key={a.id}
                    disabled={relocating}
                    onClick={() => handleRelocateClip(a.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm rounded-lg transition-colors text-[var(--rq-text-soft)] hover:bg-white/5 hover:text-[var(--rq-text)] disabled:opacity-50"
                  >
                    <div className="w-12 h-8 rounded bg-black/30 overflow-hidden flex-shrink-0 flex items-center justify-center">
                      {a.thumbnail_url ? (
                        <img src={a.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Film className="w-3.5 h-3.5 text-[var(--rq-text-soft)]" />
                      )}
                    </div>
                    <span className="truncate flex-1">{a.original_filename}</span>
                    {relocating && <Loader2 className="w-4 h-4 animate-spin" />}
                  </button>
                ))}
              {videoAssets.length <= 1 && (
                <p className="text-sm text-[var(--rq-text-soft)] py-4 text-center">No other videos available</p>
              )}
            </div>
          </div>
        </div>
      )}

      <MediaPreviewModal preview={preview} onClose={closePreview} />
    </div>
  )
}
